import { useState } from "react";
import { Alert as Notice, InputNumber, Modal, Select, Switch, Table } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getRules, simulateRule, updateRule } from "../../api/monitoring";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import { CHANNEL_LABELS, SeverityBadge, TIER_LABELS, timeAgo } from "./shared";

const TIERS = ["unverified", "bvn_verified"];
const btn = "rounded-[5px] px-4 py-2 font-bold text-[13px] disabled:opacity-50 whitespace-nowrap";

// "Withdrawal of ₦{minAmount} or more" -> "Withdrawal of ₦500,000 or more"
const fill = (template, params) =>
  template ? template.replace(/\{(\w+)\}/g, (_, k) => Number(params[k] ?? 0).toLocaleString()) : "";
const thresholds = (r) => fill(r.summaryTemplate, r.params);

function EditRule({ rule, onClose }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useState({ ...rule.params });
  const [overrides, setOverrides] = useState({
    unverified: { ...(rule.tierOverrides?.unverified || {}) },
    bvn_verified: { ...(rule.tierOverrides?.bvn_verified || {}) },
  });
  const [days, setDays] = useState(30);
  const [sim, setSim] = useState(null);

  const body = { params, tierOverrides: overrides };
  const save = useMutation(updateRule, {
    onSuccess: () => {
      showSuccessToast("Rule saved");
      queryClient.invalidateQueries("monitoring-rules");
      onClose();
    },
    onError: (e) => showErrorToast(e.message),
  });
  const simulate = useMutation(simulateRule, { onSuccess: setSim, onError: (e) => showErrorToast(e.message) });

  const setOverride = (tier, k, v) =>
    setOverrides((o) => {
      const next = { ...o, [tier]: { ...o[tier] } };
      if (v === null || v === undefined || v === "") delete next[tier][k];
      else next[tier][k] = v;
      return next;
    });

  return (
    <Modal
      open
      width={820}
      title={rule.name}
      onCancel={onClose}
      okText={save.isLoading ? "Saving…" : "Save"}
      onOk={() => save.mutate({ key: rule.key, ...body })}
    >
      <div className="space-y-4 py-1">
        <div className="text-[13px] text-[#00000099]">{rule.description}</div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[#00000080]">
              <th className="py-1 font-normal">Setting</th>
              <th className="py-1 font-normal">Everyone</th>
              {TIERS.map((t) => (
                <th key={t} className="py-1 font-normal">
                  {TIER_LABELS[t]} <span className="text-[11px]">(blank = same)</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(rule.defaults.params).map((k) => (
              <tr key={k}>
                <td className="py-1 pr-2">
                  <div>{rule.paramInfo?.[k] || k}</div>
                  <div className="text-[11px] text-[#00000080]">default {Number(rule.defaults.params[k]).toLocaleString()}</div>
                </td>
                <td className="py-1 pr-2">
                  <InputNumber min={0} value={params[k]} onChange={(v) => setParams((p) => ({ ...p, [k]: v }))} className="w-full" />
                </td>
                {TIERS.map((t) => (
                  <td key={t} className="py-1 pr-2">
                    <InputNumber min={0} value={overrides[t][k]} onChange={(v) => setOverride(t, k, v)} className="w-full" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="bg-[#F7F8FA] rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="font-bold text-[13px]">Test these settings on past transactions</div>
            <Select
              value={days}
              onChange={setDays}
              className="w-[120px]"
              options={[7, 14, 30, 60, 90].map((d) => ({ value: d, label: `Last ${d} days` }))}
            />
            <button
              type="button"
              disabled={simulate.isLoading}
              className={`bg-dark text-white ${btn}`}
              onClick={() => simulate.mutate({ key: rule.key, ...body, days })}
            >
              {simulate.isLoading ? "Running…" : "Simulate"}
            </button>
          </div>
          {sim && (
            <div className="space-y-2 text-[13px]">
              <div>
                Would have raised <b>{sim.alerts}</b> alert{sim.alerts === 1 ? "" : "s"} for <b>{sim.customers}</b> customer
                {sim.customers === 1 ? "" : "s"} in {sim.days} days — about <b>{sim.alertsPerDay}</b> a day ({sim.evaluated.toLocaleString()} checks).
              </div>
              {sim.truncated && <Notice type="warning" showIcon message="Too many transactions; only the most recent were tested." />}
              {sim.sample.length > 0 && (
                <div className="max-h-[200px] overflow-y-auto border border-[#E6E7E9] rounded bg-white">
                  {sim.sample.map((s, i) => (
                    <div key={i} className="px-3 py-1 border-b border-[#F2F2F2] last:border-0">
                      <span className="font-bold">{s.name || s.userId}</span>{" "}
                      <span className="text-[#00000080]">{new Date(s.at).toLocaleString()}</span>
                      <div>{s.summary}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

const Rules = () => {
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const canConfigure = getModulePermission("monitoring", "canConfigure");
  const [editing, setEditing] = useState(null);
  const { data: rules, isFetching } = useQuery("monitoring-rules", getRules);

  const update = useMutation(updateRule, {
    onSuccess: () => queryClient.invalidateQueries("monitoring-rules"),
    onError: (e) => showErrorToast(e.message),
  });

  const confirmHold = (r) =>
    Modal.confirm({
      title: `Hold payments for "${r.name}"?`,
      content:
        "Matching bank withdrawals and crypto sends will be debited but not sent until someone reviews them (or the hold timeout in Settings runs out).",
      okText: "Hold payments",
      onOk: () => update.mutateAsync({ key: r.key, action: "hold" }),
    });

  const columns = [
    {
      title: "Rule",
      key: "rule",
      render: (_, r) => (
        <div>
          <div className="font-bold text-[14px]">{r.name}</div>
          <div className="text-[12px] text-[#00000099]">{r.description}</div>
          <div className="text-[11px] text-[#00000080] mt-1">
            {r.stage === "pre" ? "Checked before the transaction" : "Pattern check after activity"}
            {r.channels ? ` · ${r.channels.map((c) => CHANNEL_LABELS[c]).join(", ")}` : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Thresholds",
      key: "params",
      render: (_, r) => (
        <div className="text-[12px]">
          <div>{thresholds(r)}</div>
          {TIERS.filter((t) => Object.keys(r.tierOverrides?.[t] || {}).length).map((t) => (
            <div key={t} className="text-[#00000080]">
              {TIER_LABELS[t]}: {fill(r.summaryTemplate, { ...r.params, ...r.tierOverrides[t] })}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Severity",
      key: "severity",
      render: (_, r) =>
        canConfigure ? (
          <Select
            size="small"
            value={r.severity}
            onChange={(severity) => update.mutate({ key: r.key, severity })}
            className="w-[100px]"
            options={["low", "medium", "high"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))}
          />
        ) : (
          <SeverityBadge severity={r.severity} />
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) =>
        r.supportsHold && canConfigure ? (
          <Select
            size="small"
            value={r.action}
            onChange={(action) => (action === "hold" ? confirmHold(r) : update.mutate({ key: r.key, action }))}
            className="w-[130px]"
            options={[
              { value: "alert", label: "Alert only" },
              { value: "hold", label: "Hold payment" },
            ]}
          />
        ) : (
          <span className="text-[13px]">{r.action === "hold" ? "Hold payment" : "Alert only"}</span>
        ),
    },
    {
      title: "On",
      key: "enabled",
      render: (_, r) => (
        <Switch checked={r.enabled} disabled={!canConfigure} onChange={(enabled) => update.mutate({ key: r.key, enabled })} />
      ),
    },
    {
      title: "",
      key: "edit",
      render: (_, r) => (
        <div className="text-right">
          {canConfigure && (
            <button type="button" className="text-[#4A4DE6] text-[13px]" onClick={() => setEditing(r)}>
              Edit & simulate
            </button>
          )}
          {r.updatedBy && (
            <div className="text-[11px] text-[#00000080]">
              {r.updatedBy} · {timeAgo(r.updatedAt)}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-5 space-y-3">
      <div className="text-[13px] text-[#00000080]">
        Thresholds can differ by KYC level: customers without a verified BVN vs. customers with one. Only bank withdrawal
        and crypto send rules can hold payments; every other rule raises alerts.
      </div>
      <Table rowKey="key" loading={isFetching} dataSource={rules || []} columns={columns} pagination={false} />
      {editing && <EditRule rule={editing} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default Rules;
